import type { ProcessFlow, ProcessFlowEdge, ProcessFlowLayout, ProcessFlowNode } from '~/common/model/api/batch-type/process-flow';

import { PROCESS_FLOW_NODE_DISTANCE } from '~/common/constants/feature/settings/batch-type-library';

import type { InitLayoutOptions } from './_data.type';

import { createEdge } from './_edge';
import { beautifyOffset } from './_layout';
import { createNode } from './_node';

/**
 * Create initial ProcessFlow.
 *
 * @returns {ProcessFlow} Initial ProcessFlow.
 */
export function createInitialProcessFlow(): ProcessFlow {
  // Start
  const startNodeOffset = beautifyOffset('start');
  const startNode = createNode.start({
    position: {
      x: 0,
      y: 0,
    },
    offset: startNodeOffset,
  });

  // End
  const endNodeOffset = beautifyOffset('end', {
    x: -startNodeOffset.x,
    y: -startNodeOffset.y,
  });
  const endNode = createNode.end({
    position: {
      x: startNode.position.x,
      y: startNode.position.y + PROCESS_FLOW_NODE_DISTANCE,
    },
    offset: endNodeOffset,
  });

  return {
    layout: {
      nodes: [startNode, endNode],
      edges: [
        // Start -> End
        createEdge.default({
          source: startNode.id,
          target: endNode.id,
          data: {
            breakable: false,
          },
        }),
      ],
    },
    steps: [],
  };
}

/**
 * Init ProcessFlow Layout.
 *
 * @param {ProcessFlowLayout} layout Initial layout.
 * @param {InitLayoutOptions} options Options.
 *
 * @returns {ProcessFlowLayout} ProcessFlow Layout.
 */
export function initLayout(layout: ProcessFlowLayout, options: InitLayoutOptions): ProcessFlowLayout {
  return {
    nodes: layout.nodes.map((node) => {
      const assignOptions = options.node[node.type];
      if (assignOptions) {
        node.data = Object.assign<ProcessFlowNode['data']>(node.data, assignOptions);
      }

      return node;
    }),

    edges: layout.edges.map((edge) => {
      const assignOptions = options.edge[edge.type];
      if (assignOptions) {
        edge.data = Object.assign<ProcessFlowEdge['data']>(edge.data, assignOptions);
      }

      return edge;
    }),
  };
}
